import { html } from "lit";
import { customElement, property } from "lit/decorators.js";

import { inject } from "ui-common/di";
import type { ICreatorSelectState, ICreatorSelectService, CreatorItem } from "ui-common/services/creator-select/types";
import type { ICoPromotionSearchState, ICoPromotionSearchService } from "ui-common/services/co-promotion-search/types";
import { CreatorSelectList } from "ui-components/creator-select-list/creator-select-list";

import "ui-components/modal/wow-modal";
import "ui-components/button/wow-button";

import { styles as modalStyles } from "./find-collaborators-modal.css";
import { translate } from "../../translations/i18n";

@customElement("find-collaborators-modal")
export class FindCollaboratorsModal extends CreatorSelectList {
  static override styles = [...CreatorSelectList.styles, modalStyles];

  @property({ type: Array })
  existingCollaborators: CreatorItem[] = [];

  @inject<ICreatorSelectState>("creatorSelectState")
  protected selectState!: ICreatorSelectState;

  @inject<ICreatorSelectService>("creatorSelectService")
  protected selectService!: ICreatorSelectService;

  @inject<ICoPromotionSearchState>("coPromotionSearchState")
  private searchState!: ICoPromotionSearchState;

  @inject<ICoPromotionSearchService>("coPromotionSearchService")
  private searchService!: ICoPromotionSearchService;

  override connectedCallback() {
    super.connectedCallback();
    this.placeholder = translate("findCollaborators.searchPlaceholder");
    this.emptyTitle = translate("findCollaborators.emptyTitle");
    this.emptySubtitle = translate("findCollaborators.emptySubtitle");
    this.addLabel = translate("findCollaborators.add");

    if (this.existingCollaborators.length > 0) {
      this.selectService.setInitialSelection(this.existingCollaborators);
    } else {
      this.selectService.reset();
    }

    this.searchService.loadSuggested();
  }

  protected get creators(): CreatorItem[] {
    return this.searchState.creators.get().map((c) => ({
      id: c.userId,
      handle: c.handle,
      imageUrl: c.avatarUrl ?? undefined,
    }));
  }

  protected get isLoading(): boolean {
    return this.searchState.loading.get();
  }

  protected onSearch(query: string): void {
    this.searchService.search(query);
  }

  private get hasSelection(): boolean {
    return this.selectState.selectedIds.get().size > 0;
  }

  private handleSave() {
    const selectedItems = this.selectState.selectedItems.get();
    const handles = selectedItems.map((item) => item.handle).filter((handle): handle is string => handle !== null);

    this.dispatchEvent(
      new CustomEvent("collaborators-saved", {
        detail: { collaborators: handles },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private handleClose() {
    this.selectService.reset();
    this.searchService.clear();
    this.dispatchEvent(
      new CustomEvent("close", {
        bubbles: true,
        composed: true,
      }),
    );
  }

  protected override render() {
    return html`
      <wow-modal variant="responsive" mobile-fullscreen hide-primary-button @close=${this.handleClose}>
        <span slot="header">${translate("findCollaborators.title")}</span>

        ${super.render()}

        <div class="collaborators-footer" slot="one-column">
          <wow-button class="cancel-button" variant="neutral" size="medium" @click=${this.handleClose}>
            ${translate("common.cancel")}
          </wow-button>
          <wow-button
            class="save-button"
            variant="primary"
            size="medium"
            ?disabled=${!this.hasSelection}
            @click=${this.handleSave}
          >
            ${translate("common.save")}
          </wow-button>
        </div>
      </wow-modal>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "find-collaborators-modal": FindCollaboratorsModal;
  }
}
